import React, { use, useEffect, useRef, useState } from "react";
import { Item, ComboBox, useAsyncList } from "@adobe/react-spectrum";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { useTranslation } from "react-i18next";
import { ListItemTypeRequest } from "../../../../../services/soap/item/requests/ListItemTypeRequest";
import { ListItemRequest } from "../../../../../services/soap/item/requests/ListItemRequest";

interface products {
  aclh?: string;
  amount?: string;
  parentAmount?: string;
  amountWithVat?: string;
  billingDate?: string;
  componentCategory?: string;
  componentCategoryName?: string;
  created?: string;
  createdBy?: string;
  createdByName?: string;
  creditAccount?: string;
  creditAccountName?: string;
  currency?: string;
  currencyName?: string;
  debitAccount?: string;
  debitAccountName?: string;
  field0?: string;
  field0_id?: string;
  field1?: string;
  field1_id?: string;
  field2?: string;
  field2_id?: string;
  field3?: string;
  field3_id?: string;
  field4?: string;
  field4_id?: string;
  field5?: string;
  field5_id?: string;
  field6?: string;
  field6_id?: string;
  field7?: string;
  field7_id?: string;
  field8?: string;
  field8_id?: string;
  field9?: string;
  field9_id?: string;
  folderId?: string;
  formId?: string;
  formName?: string;
  formValue?: string;
  id: number;
  manufacturer?: string;
  manufacturerAclh?: string;
  manufacturerName?: string;
  manufacturerStatus?: string;
  manufacturerStatusColor?: string;
  manufacturerStatusName?: string;
  modified?: string;
  modifiedBy?: string;
  modifiedByName?: string;
  name: string;
  note?: string;
  priceType: string;
  priceTypeName: string;
  purchasePrice: string;
  quantity?: string;
  refId?: string;
  status?: string;
  statusShow?: string;
  statusName?: string;
  templateAclh?: string;
  treeHandle?: string;
  type: string;
  typeName: string;
  units?: string;
  unitsName?: string;
  vat?: string;
  vatName?: string;
  vatValue?: string;
  ratio?: string;
  dimension1name?: string;
  dimension1?: string;
  dimension2name?: string;
  dimension2?: string;
  dimension3name?: string;
  dimension3?: string;
  costSplitSwitch?: string;
  revenueSplitSwitch?: string;
  measureStock?: string;
  openingBalanceQty?: string;
  openingBalanceDate?: string;
  closingBalanceQty?: string;
  availableBalanceQty?: string;
}

interface IProductDropDownProps {
  selectedProduct: string;
  onChange: (data: products) => void;
  isRequired?: any
  errorMessage?: any
  validationState?: any
  setProductType: (data: products) => void;
}

const ProductDropDown: React.FC<IProductDropDownProps> = ({
  selectedProduct,
  onChange,
  isRequired,
  errorMessage,
  validationState,
  setProductType }) => {
  const { store, itemService } = useDependency();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const { t } = useTranslation('products');
  const _t = (key: string) => t(`addNewProduct.${key}`, { ns: 'products' });

  const inputRef = useRef<any>(null);

  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 200);
    list.reload();
  }, [])

  const handleOnBlur = () => {
    if (!selectedKey) {
      const updatedValue = {
        id: 0,
        name: '',
      } as products;
      onChange(updatedValue);
    }
  };

  const list = useAsyncList<products>({
    async load({ cursor, filterText }) {
      const offset = cursor ? Number(cursor) : 0;
      const limit = 500;
      try {
        const response: any = await itemService.listItem(new ListItemRequest(store.Server, store.SessionId, limit, offset, { searchName: filterText }));
        let itemList: any[] = [];
        if (response.result === 'OK' && response?.count > 0) {
          if (Array.isArray(response.ITEM)) {
            itemList = response.ITEM;
          } else if (response.ITEM) {
            // If response.ITEM is a single object
            itemList = [response.ITEM];
          }
        }
        return {
          items: itemList,
          cursor: itemList.length === limit ? String(offset + limit) : undefined,
        };
      } catch (error) {
        console.error("Error fetching items:", error);
        return { items: [] };
      }
    },
  });


  useEffect(() => {
    if (selectedProduct) {
      setSelectedKey(selectedProduct);
      list.reload();
    } else {
      setSelectedKey(null);
    }
  }, [selectedProduct]);

  const handleSelectionChange = (key: any) => {
    setSelectedKey(key);
    if (key) {
      const selectedProduct = list.items.find((x) => x.id === key);
      if (selectedProduct) {
        onChange(selectedProduct);
      }
    }
  };

  useEffect(() => {
    if (selectedKey) {
      const selectedProduct = list.items.find((x) => x.id.toString() === selectedKey);
      if (selectedProduct) {
        onChange(selectedProduct);
      }
    }
  }, [list.items]);

  return (
    <ComboBox
      items={list.items}
      onInputChange={list.setFilterText}
      loadingState={list.loadingState}
      onLoadMore={list.loadMore}
      selectedKey={selectedKey?.toString()}
      onSelectionChange={handleSelectionChange}
      onBlur={handleOnBlur}
      isRequired={isRequired}
      errorMessage={errorMessage}
      validationState={validationState}
      width={{ L: '100%', base: '100%' }}
      ref={inputRef}
    >
      {(item) => (
        <Item key={item.id} textValue={item.name}>
          {item.name}
        </Item>
      )}
    </ComboBox>
  );
};

export default ProductDropDown;
